// React & state
import React, { useEffect, useState } from "react";

// Date libs
import dayjs from "dayjs";
import "dayjs/locale/es";
import "moment/locale/es";

// Formik
import { Formik } from "formik";

// MUI core
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Modal,
  Snackbar,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

// MUI X (pickers & datagrid)
import { DataGrid } from "@mui/x-data-grid";

// Redux
import { useSelector } from "react-redux";

// Shepherd
import "shepherd.js/dist/css/shepherd.css";

// Theme
import { tokens } from "../../theme";

// Project components
import Header from "../../components/Header";
import ModalAvisoGenerado from "./components/ModalAvisoGenerado";

// Services
import {
  getAvisosPendientes,
  getServicios,
  getValidarAporteMes,
  getValidarAvisosMes,
  getExisteAvisoDelMes,
  insertAvisoCobro,
} from "../../services/index";

// Styles & helpers
import { dataGridStylesAvisos } from "../../styles/styles";
import { handleGenerarPDF } from "../../utils/pdfUtils";

// Data & form
import { availableMonths, availableYears } from "../../data/staticData";
import { initialValues } from "../../data/initialValues";
import { validationSchema } from "../../data/validationSchemas";

// Local utils
import { calculateMontoPagarYMora } from "./utils/calculateMonto";
import { makeValidateMonthYearWrapper } from "./utils/validateMonthYear";
import { columnsAvisosPend } from "./datagrid/columnsAvisosPend";
import AvisoFields from "./form/AvisoFields";

// Tour
import { useTafiraTour } from "./hooks/useTafiraTour";

dayjs.locale("es");

// ─── Wrapper ──────────────────────────────────────────────────────────────────
const TarifaSupervision = () => {
  return <TarifaSupervisionContent />;
};

export default TarifaSupervision;

// ─── Inner component: uses ShepherdTourContext ────────────────────────────────
const TarifaSupervisionContent = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  let urlSaccWeb = process.env.REACT_APP_URL_SACCWEB;

  // Tour
  const tour = useTafiraTour();
  const tourCompleted = useSelector((state) => state.tourTafiraCompleted);

  useEffect(() => {
    if (!tourCompleted && tour) {
      const timer = setTimeout(() => tour.start(), 500);
      return () => clearTimeout(timer);
    }
  }, [tour, tourCompleted]);

  // Estado de Snackbar y validación del botón Guardar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isGuardarDisabled, setIsGuardarDisabled] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Estado de opciones y datos obtenidos del backend
  const [tipoServicio, setTipoServicio] = useState([]);
  const [listaAvisos, setListaAvisos] = useState([]);
  const [avisoCobro, setAvisoCobro] = useState(0);
  const usuarioSitae = useSelector((state) => state.user);
  const [open, setOpen] = useState(false);
  const [showModalAviso, setShowModalAviso] = useState(false);
  const [isAporteDisabled, setIsAporteDisabled] = useState(false);
  const [mostrarAporte, setMostrarAporte] = useState(false);
  const [usarSufijo, setUsarSufijo] = useState(usuarioSitae === "AT0001");
  const [impersonatedUser, setImpersonatedUser] = useState("");
  const effectiveUser =
    usuarioSitae === "AT0001" && impersonatedUser.trim()
      ? impersonatedUser.trim().toUpperCase()
      : usuarioSitae;

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getSnackDuration = (severity, msg) => {
    const len = msg?.length || 0;
    const extra = Math.min(4000, Math.max(0, len - 80) * 40); // +~0–4s si es largo

    switch (severity) {
      case "success":
        return 4000 + extra;
      case "info":
        return 5000 + extra;
      case "warning":
        return 7000 + extra;
      case "error":
        return null; // permanece hasta cerrar
      default:
        return 6000 + extra;
    }
  };

  const validateMonthYear = makeValidateMonthYearWrapper({
    usuarioSitae: effectiveUser,
    getExisteAvisoDelMes: (op, mes, anio, svc) => getExisteAvisoDelMes(op, mes, anio, svc, usarSufijo),
    getValidarAvisosMes: (op, mes, anio, svc) => getValidarAvisosMes(op, mes, anio, svc, usarSufijo),
    getValidarAporteMes: (op, mes, anio, svc) => getValidarAporteMes(op, mes, anio, svc, usarSufijo),
    setIsAporteDisabled,
    setMostrarAporte,
    setIsGuardarDisabled,
  });

  const handleSubmit = async (values, { resetForm }) => {
    // Evitar múltiples clics mientras se procesa
    if (isGuardarDisabled) return;

    setIsProcessing(true);
    setIsGuardarDisabled(true);

    const firstDayOfMonth = dayjs()
      .year(values.anio)
      .month(values.mes - 1)
      .date(1);

    const lastDayOfMonth = firstDayOfMonth.endOf("month");

    values.codigoOperador = effectiveUser;
    values.sistemaUsuario = usuarioSitae;

    const monthNames = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];

    const vencMes = Number(values.mes); // mes de vencimiento
    const vencAnio = Number(values.anio); // año de vencimiento
    const reportedAnio = vencMes === 1 ? vencAnio : vencAnio;
    const descripcionPeriodo =
      `Periodo del mes de: ${monthNames[values.mes - 1]} del año ${reportedAnio}. ` +
      `Monto declarado: ${values.ingresoServicio}`;

    const detalle = [
      // Si hay mora, agregar conceptos de mora
      ...(values.mora > 0
        ? [
            {
              codigoConceptoCobro: "CC-20",
              valor: values.mora,
              descripcion: `Mora por Cobro Automático: desde ${dayjs(values.fechaVencimiento)
                .locale("es")
                .format("DD-MMMM-YYYY")} hasta ${dayjs(values.fechaPago)
                .locale("es")
                .format("DD-MMMM-YYYY")}`,
              periodoInicio: dayjs(firstDayOfMonth).format("YYYY-MM-DD"),
              periodoFin: dayjs(lastDayOfMonth).format("YYYY-MM-DD"),
            },
            ...(mostrarAporte
              ? [
                  {
                    codigoConceptoCobro: "FF-02",
                    valor: values.moraAporte,
                    descripcion: `Mora por Cobro Automático: desde ${dayjs(values.fechaVencimiento)
                      .locale("es")
                      .format("DD-MMMM-YYYY")} hasta ${dayjs(values.fechaPago)
                      .locale("es")
                      .format("DD-MMMM-YYYY")}`,
                    periodoInicio: dayjs(firstDayOfMonth).format("YYYY-MM-DD"),
                    periodoFin: dayjs(lastDayOfMonth).format("YYYY-MM-DD"),
                  },
                ]
              : []),
          ]
        : []),

      // Concepto principal (obligatorio)
      {
        codigoConceptoCobro: "CC-06",
        valor: values.montoPagar,
        descripcion: descripcionPeriodo,
        periodoInicio: firstDayOfMonth.format("YYYY-MM-DD"),
        periodoFin: lastDayOfMonth.format("YYYY-MM-DD"),
      },

      // Aporte 1% solo si se activa
      ...(mostrarAporte
        ? [
            {
              codigoConceptoCobro: "FF-01",
              valor: values.montoAporte,
              descripcion: descripcionPeriodo,
              periodoInicio: firstDayOfMonth.format("YYYY-MM-DD"),
              periodoFin: lastDayOfMonth.format("YYYY-MM-DD"),
            },
          ]
        : []),
    ];

    values.detalles = detalle;
    console.log(values);

    try {
      const avisoCobroResponse = await insertAvisoCobro(values, usarSufijo);

      if (avisoCobroResponse.ok) {
        const uploadFileResponse = await subirArchivo(values, avisoCobroResponse.codigoAviso);
        setAvisoCobro(avisoCobroResponse.codigoAviso);

        console.log("[tafiraSupervision] Generating PDF for aviso:", avisoCobroResponse.codigoAviso);
        console.log(
          "[tafiraSupervision] Using avisoData from insert response:",
          avisoCobroResponse.avisoData ? "Yes" : "No",
        );

        // Pass avisoData directly from insert response to avoid separate query
        const pdfResult = await handleGenerarPDF(
          avisoCobroResponse.codigoAviso,
          avisoCobroResponse.avisoData,
        );
        console.log("[tafiraSupervision] PDF generation result:", pdfResult);

        if (pdfResult && pdfResult.success) {
          console.log("[tafiraSupervision] PDF generated successfully");
        } else {
          console.error("[tafiraSupervision] PDF generation failed:", pdfResult?.error);
          setSnackbarSeverity("warning");
          setSnackbarMessage(
            "Aviso guardado pero el PDF no pudo generarse. Error: " +
              (pdfResult?.error?.message || "Error desconocido"),
          );
          setSnackbarOpen(true);
        }

        if (uploadFileResponse.ok) {
          setSnackbarSeverity("success");
          setSnackbarMessage("La información y archivo se guardaron correctamente");
          setShowModalAviso(true);
          resetForm();
          setMostrarAporte(false);
          setIsAporteDisabled(false);
        } else {
          setSnackbarSeverity("error");
          setSnackbarMessage(uploadFileResponse.message || "Error al subir el archivo");
        }
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage("Error al guardar la información de aviso de cobro");
      }

      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error en la solicitud:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Error en la solicitud");
      setSnackbarOpen(true);
    } finally {
      // Habilitar el botón al finalizar la operación
      setTimeout(() => {
        setIsGuardarDisabled(false);
        setIsProcessing(false);
      }, 3000);
    }
  };

  const subirArchivo = async (values, codigoAviso) => {
    if (!values.detalleIngreso || values.detalleIngreso.length === 0) {
      return { ok: false };
    }
    const formData = new FormData();
    values.detalleIngreso.forEach((file) => formData.append("detalleIngreso", file));
    formData.append("codigoOperador", effectiveUser);
    formData.append("numeroAviso", codigoAviso);
    formData.append("sistemaUsuario", usuarioSitae);

    try {
      const response = await fetch(urlSaccWeb + "subirArchivo", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const message = (await response.text().catch(() => "")).trim();
        return { ok: false, message };
      }

      return { ok: true };
    } catch (error) {
      console.error("Error al subir el archivo:", error);
      throw error;
    }
  };

  // Fetch de servicios y avisos pendientes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const servicios = await getServicios(effectiveUser);
        setTipoServicio(servicios);

        const avisos = await getAvisosPendientes(effectiveUser, usarSufijo);
        setListaAvisos(avisos);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [effectiveUser, usarSufijo]);

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="FACTURACIÓN TARIFA POR SERVICIOS DE SUPERVISIÓN"
          subtitle="Registro para generar su aviso de cobro por servicios de supervisión"
        />
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<HelpOutlineIcon />}
          onClick={() => tour && tour.start()}
          size="small"
        >
          Tour
        </Button>
      </Box>
      {usuarioSitae === "AT0001" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            px: 2,
            py: 1,
            border: "1px solid",
            borderColor: "warning.dark",
            borderRadius: 1,
            bgcolor: "warning.dark",
            opacity: 0.85,
            width: "fit-content",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={usarSufijo}
                onChange={(e) => setUsarSufijo(e.target.checked)}
                color="warning"
                size="small"
              />
            }
            label={
              <Typography variant="caption" color="warning.contrastText" fontWeight="bold">
                {usarSufijo ? "BD Prueba" : "BD Producción"}
              </Typography>
            }
            sx={{ mr: 0 }}
          />
          <Divider orientation="vertical" flexItem sx={{ borderColor: "warning.light", opacity: 0.4 }} />
          <TextField
            label="Simular operador"
            variant="filled"
            size="small"
            value={impersonatedUser}
            onChange={(e) => setImpersonatedUser(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && setImpersonatedUser(impersonatedUser.trim().toUpperCase())}
            sx={{
              maxWidth: 200,
              "& .MuiFilledInput-root": { bgcolor: "rgba(0,0,0,0.2)" },
              "& .MuiInputLabel-root": { color: "warning.contrastText" },
              "& .MuiFilledInput-input": { color: "warning.contrastText" },
            }}
          />
          {impersonatedUser.trim() && (
            <Chip
              label={`Viendo como: ${impersonatedUser.trim()}`}
              color="warning"
              size="small"
              variant="outlined"
              sx={{ fontWeight: "bold", borderColor: "warning.contrastText", color: "warning.contrastText" }}
            />
          )}
        </Box>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, errors, touched }) => {
          return (
            <AvisoFields
              colors={colors}
              theme={theme}
              tipoServicio={tipoServicio}
              availableYears={availableYears}
              availableMonths={availableMonths}
              validateMonthYear={validateMonthYear}
              calculateMontoPagarYMora={calculateMontoPagarYMora}
              mostrarAporte={mostrarAporte}
              setMostrarAporte={setMostrarAporte}
              isAporteDisabled={isAporteDisabled}
              isProcessing={isProcessing}
              isGuardarDisabled={isGuardarDisabled}
              listaAvisos={listaAvisos}
              onOpenDocs={handleOpen}
              notify={(severity, message) => {
                setSnackbarSeverity(severity);
                setSnackbarMessage(message);
                setSnackbarOpen(true);
              }}
            />
          );
        }}
      </Formik>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={getSnackDuration(snackbarSeverity, snackbarMessage)}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          top: { xs: 12, sm: 16 },
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            minWidth: { xs: 320, sm: 420 },
            boxShadow: 6,
            borderRadius: 2,
            "& .MuiAlert-icon": {
              fontSize: "1.6rem",
              mr: 1.25,
            },
            "& .MuiAlert-message": {
              fontSize: "0.95rem",
              fontWeight: 600,
              lineHeight: 1.35,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "70vw",
            maxWidth: "900px",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 4,
          }}
        >
          {/* Título del Modal */}
          <Typography id="modal-title" variant="h3" gutterBottom>
            {listaAvisos && listaAvisos.length > 0 ? "Avisos con Documentación Incompleta" : " "}
          </Typography>

          <Box
            sx={{
              ...dataGridStylesAvisos(colors),
              borderRadius: 2,
              boxShadow: 3,
              bgcolor: colors.primary[400],
            }}
          >
            {listaAvisos && listaAvisos.length > 0 ? (
              <DataGrid
                sx={{ height: "400px", borderRadius: 2 }}
                rows={listaAvisos}
                columns={columnsAvisosPend}
                autoHeight={false}
              />
            ) : (
              <Box
                height="400px"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                p={3}
              >
                <img
                  src="https://cdn-icons-png.flaticon.com/512/4076/4076549.png"
                  alt="Sin avisos"
                  width={100}
                  height={100}
                  style={{ marginBottom: "16px", opacity: 0.7 }}
                />
                <Typography variant="h6" fontWeight="bold" color={colors.grey[300]}>
                  No hay avisos pendientes
                </Typography>
                <Typography variant="body2" color={colors.grey[400]}>
                  Por ahora no tienes acciones pendientes relacionadas con avisos de cobro.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Botón para cerrar */}
          <Box sx={{ textAlign: "right", mt: 3 }}>
            <Button onClick={handleClose} color="secondary" variant="contained">
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>

      <ModalAvisoGenerado
        open={showModalAviso}
        onClose={() => setShowModalAviso(false)}
        codigoAviso={avisoCobro}
        colors={colors}
      />
    </Box>
  );
};
